import { RSVP } from '../types.ts';

const API_BASE_URL = '/api/rsvps'; // Placeholder for your backend API endpoint

/**
 * Fetches all RSVP records from the server.
 * @returns A promise that resolves to an array of RSVP objects.
 */
export const fetchRsvps = async (): Promise<RSVP[]> => {
  try {
    const response = await fetch(API_BASE_URL);

    if (!response.ok) {
      let errorMessage = `Server error (${response.status}): ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData && typeof errorData === 'object') {
          // If it's JSON but no specific message field, stringify it
          errorMessage = JSON.stringify(errorData);
        }
      } catch (jsonError) {
        // If response is not JSON, try to get raw text
        try {
          const textError = await response.text();
          if (textError) {
            errorMessage = textError;
          }
        } catch (textReadError) {
          // Fallback to generic HTTP status text if body can't be read
        }
      }
      throw new Error(`Failed to fetch RSVPs: ${errorMessage}`);
    }
    const rsvps: RSVP[] = await response.json();
    return rsvps;
  } catch (error: any) {
    console.error("Error fetching RSVPs from server:", error);
    // Differentiate between network errors (e.g., server unreachable) and other errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error("Network error: Could not connect to the RSVP server. Please ensure the server is running and accessible.");
    } else {
      // Re-throw the refined error message from above, or a new generic one
      throw new Error(error.message || "Failed to load RSVPs from the server. Please try again later.");
    }
  }
};

/**
 * Creates a new RSVP record on the server.
 * @param newRSVP The RSVP object (without an ID, as it will be generated by the server).
 * @returns A promise that resolves to the created RSVP object, including the server-generated ID.
 */
export const createRsvp = async (newRSVP: Omit<RSVP, 'id'>): Promise<RSVP> => {
  try {
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(newRSVP),
    });

    if (!response.ok) {
      let errorMessage = `Server error (${response.status}): ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData && typeof errorData === 'object') {
          // If it's JSON but no specific message field, stringify it
          errorMessage = JSON.stringify(errorData);
        }
      } catch (jsonError) {
        // If response is not JSON, try to get raw text
        try {
          const textError = await response.text();
          if (textError) {
            errorMessage = textError;
          }
        } catch (textReadError) {
          // Fallback to generic HTTP status text if body can't be read
        }
      }
      throw new Error(`Failed to create RSVP: ${errorMessage}`);
    }
    const createdRsvp: RSVP = await response.json();
    return createdRsvp;
  } catch (error: any) {
    console.error("Error creating RSVP on server:", error);
    // Differentiate between network errors (e.g., server unreachable) and other errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error("Network error: Could not connect to the RSVP server. Please ensure the server is running and accessible.");
    } else {
      // Re-throw the refined error message from above, or a new generic one
      throw new Error(error.message || "Failed to submit RSVP. Please try again.");
    }
  }
};